import Plotly from 'plotly.js-dist-min';
import React, { useEffect, useRef, useCallback } from 'react';

interface PlotlyViewerProps {
  plotJson: string;
  fileName?: string;
}

const PlotlyViewer: React.FC<PlotlyViewerProps> = ({ plotJson, fileName }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const plotRef = useRef<HTMLDivElement>(null);

  // Calculate scaled font size based on container dimensions
  const getScaledFontSize = useCallback((baseSize: number, width: number, height: number) => {
    const minDimension = Math.min(width, height);
    // Scale factor: at 400px use base size, scale proportionally
    const scaleFactor = Math.max(0.5, Math.min(1.2, minDimension / 400));
    return Math.round(baseSize * scaleFactor);
  }, []);

  // Function to scale the modebar icons
  const updateModebarScale = useCallback((container: HTMLElement, width: number, height: number) => {
    const minDimension = Math.min(width, height);
    const scale = Math.max(0.5, Math.min(1, minDimension / 350));

    const modebar = container.querySelector('.modebar') as HTMLElement;
    if (modebar) {
      modebar.style.transform = `scale(${scale})`;
      modebar.style.transformOrigin = 'top right';
    }
  }, []);

  // Function to resize Plotly to fit container
  const resizePlot = useCallback(() => {
    if (plotRef.current && containerRef.current) {
      const { clientWidth, clientHeight } = containerRef.current;
      if (clientWidth > 0 && clientHeight > 0) {
        const titleFontSize = getScaledFontSize(16, clientWidth, clientHeight);
        const axisFontSize = getScaledFontSize(12, clientWidth, clientHeight);
        const tickFontSize = getScaledFontSize(10, clientWidth, clientHeight);
        const legendFontSize = getScaledFontSize(10, clientWidth, clientHeight);
        const annotationFontSize = getScaledFontSize(10, clientWidth, clientHeight);
        const colorbarFontSize = getScaledFontSize(10, clientWidth, clientHeight);

        // Build layout update object with all axes (for scatter matrices and subplots)
        const layoutUpdate: Record<string, unknown> = {
          width: clientWidth,
          height: clientHeight,
          'title.font.size': titleFontSize,
          'legend.font.size': legendFontSize,
        };

        // Get current layout to find all axes
        const currentLayout = (plotRef.current as any)?.layout || {};

        // Update all xaxis and yaxis variants (xaxis, xaxis2, xaxis3, etc.)
        Object.keys(currentLayout).forEach((key) => {
          if (key.startsWith('xaxis') || key.startsWith('yaxis')) {
            layoutUpdate[`${key}.title.font.size`] = axisFontSize;
            layoutUpdate[`${key}.tickfont.size`] = tickFontSize;
          }
          // Handle coloraxis for shared colorbars
          if (key.startsWith('coloraxis')) {
            layoutUpdate[`${key}.colorbar.title.font.size`] = colorbarFontSize;
            layoutUpdate[`${key}.colorbar.tickfont.size`] = colorbarFontSize;
          }
        });

        // Update annotation font sizes if any
        if (currentLayout.annotations) {
          layoutUpdate['annotations'] = currentLayout.annotations.map((ann: any) => ({
            ...ann,
            font: { ...ann.font, size: annotationFontSize },
          }));
        }

        Plotly.relayout(plotRef.current, layoutUpdate);

        // Update colorbar fonts on traces (for per-trace colorbars)
        const currentData = (plotRef.current as any)?.data || [];
        const traceUpdates: Record<string, unknown[]> = {};

        currentData.forEach((trace: any, index: number) => {
          if (trace.marker?.colorbar || trace.colorbar) {
            if (!traceUpdates['marker.colorbar.title.font.size']) {
              traceUpdates['marker.colorbar.title.font.size'] = new Array(currentData.length).fill(undefined);
              traceUpdates['marker.colorbar.tickfont.size'] = new Array(currentData.length).fill(undefined);
              traceUpdates['colorbar.title.font.size'] = new Array(currentData.length).fill(undefined);
              traceUpdates['colorbar.tickfont.size'] = new Array(currentData.length).fill(undefined);
            }
            traceUpdates['marker.colorbar.title.font.size'][index] = colorbarFontSize;
            traceUpdates['marker.colorbar.tickfont.size'][index] = colorbarFontSize;
            traceUpdates['colorbar.title.font.size'][index] = colorbarFontSize;
            traceUpdates['colorbar.tickfont.size'][index] = colorbarFontSize;
          }
        });

        if (Object.keys(traceUpdates).length > 0) {
          Plotly.restyle(plotRef.current, traceUpdates);
        }

        // Scale modebar icons
        updateModebarScale(containerRef.current, clientWidth, clientHeight);
      }
    }
  }, [getScaledFontSize, updateModebarScale]);

  useEffect(() => {
    if (!plotRef.current || !containerRef.current) return;

    try {
      // Parse the Plotly JSON
      const plotData = JSON.parse(plotJson);

      // Plotly JSON format typically has {data: [...], layout: {...}, config: {...}}
      const { data, layout, config } = plotData;

      // Get container dimensions for initial size
      const { clientWidth, clientHeight } = containerRef.current;

      // Calculate scaled font sizes for initial render
      const w = clientWidth || 400;
      const h = clientHeight || 300;
      const titleFontSize = getScaledFontSize(16, w, h);
      const axisFontSize = getScaledFontSize(12, w, h);
      const tickFontSize = getScaledFontSize(10, w, h);
      const legendFontSize = getScaledFontSize(10, w, h);
      const annotationFontSize = getScaledFontSize(10, w, h);
      const colorbarFontSize = getScaledFontSize(10, w, h);

      // Create layout with autosize, scaled fonts, and container dimensions
      const responsiveLayout: Record<string, any> = {
        ...layout,
        autosize: true,
        width: clientWidth || undefined,
        height: clientHeight || undefined,
        margin: layout?.margin || { l: 50, r: 50, t: 40, b: 40 },
        title: {
          ...layout?.title,
          font: { ...layout?.title?.font, size: titleFontSize },
        },
        legend: {
          ...layout?.legend,
          font: { ...layout?.legend?.font, size: legendFontSize },
        },
      };

      // Scale all axes (xaxis, xaxis2, yaxis, yaxis2, etc. for scatter matrices)
      if (layout) {
        Object.keys(layout).forEach((key) => {
          if (key.startsWith('xaxis') || key.startsWith('yaxis')) {
            responsiveLayout[key] = {
              ...layout[key],
              title: { ...layout[key]?.title, font: { ...layout[key]?.title?.font, size: axisFontSize } },
              tickfont: { ...layout[key]?.tickfont, size: tickFontSize },
            };
          }
          // Handle coloraxis for shared colorbars
          if (key.startsWith('coloraxis')) {
            responsiveLayout[key] = {
              ...layout[key],
              colorbar: {
                ...layout[key]?.colorbar,
                title: { ...layout[key]?.colorbar?.title, font: { size: colorbarFontSize } },
                tickfont: { ...layout[key]?.colorbar?.tickfont, size: colorbarFontSize },
              },
            };
          }
        });

        // Scale annotations if present
        if (layout.annotations) {
          responsiveLayout.annotations = layout.annotations.map((ann: any) => ({
            ...ann,
            font: { ...ann.font, size: annotationFontSize },
          }));
        }
      }

      // Fallback for simple plots without explicit axis definitions
      if (!responsiveLayout.xaxis) {
        responsiveLayout.xaxis = { tickfont: { size: tickFontSize } };
      }
      if (!responsiveLayout.yaxis) {
        responsiveLayout.yaxis = { tickfont: { size: tickFontSize } };
      }

      // Scale colorbar fonts on trace data (for per-trace colorbars like heatmaps, scatter with color)
      const scaledData = (data || []).map((trace: any) => {
        const scaledTrace = { ...trace };

        // Handle marker.colorbar (scatter plots with color scale)
        if (trace.marker?.colorbar) {
          scaledTrace.marker = {
            ...trace.marker,
            colorbar: {
              ...trace.marker.colorbar,
              title: { ...trace.marker.colorbar?.title, font: { size: colorbarFontSize } },
              tickfont: { ...trace.marker.colorbar?.tickfont, size: colorbarFontSize },
            },
          };
        }

        // Handle direct colorbar (heatmaps, contour plots)
        if (trace.colorbar) {
          scaledTrace.colorbar = {
            ...trace.colorbar,
            title: { ...trace.colorbar?.title, font: { size: colorbarFontSize } },
            tickfont: { ...trace.colorbar?.tickfont, size: colorbarFontSize },
          };
        }

        return scaledTrace;
      });

      // Create the plot with download and interaction tools enabled
      Plotly.newPlot(
        plotRef.current,
        scaledData,
        responsiveLayout,
        {
          responsive: true,
          displayModeBar: true,
          displaylogo: false,
          modeBarButtonsToRemove: [
            'lasso2d',
            'select2d',
          ],
          toImageButtonOptions: {
            format: 'png',
            filename: fileName || 'plot',
            height: 800,
            width: 1200,
            scale: 2,
          },
          ...config
        }
      ).then(() => {
        // Scale modebar after plot is created
        if (containerRef.current) {
          updateModebarScale(containerRef.current, clientWidth || 400, clientHeight || 300);
        }
      });
    } catch (error) {
      console.error('Error rendering Plotly plot:', error);
    }

    // Cleanup on unmount
    return () => {
      if (plotRef.current) {
        Plotly.purge(plotRef.current);
      }
    };
  }, [plotJson, fileName, getScaledFontSize, updateModebarScale]);

  // Use ResizeObserver to resize Plotly when container changes
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      resizePlot();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [resizePlot]);

  return (
    <div ref={containerRef} className="w-full h-full overflow-hidden">
      <div ref={plotRef} className="w-full h-full" />
    </div>
  );
};

export default PlotlyViewer;
